import React, { useCallback, useId, useRef, useState } from "react";
import { BadgeCheck, Truck, X } from "lucide-react";
import { TURNSTILE_SITE_KEY } from "../../config/eventsApi";
import {
  buildPickupRequestPayload,
  eventPickupEnabled,
  pickupFieldError,
  pickupRequestErrorMessage,
  submitPublicPickupRequest,
} from "../../utils/publicEventsApi";
import { toastActionError, toastActionSuccess } from "../../utils/registrationToasts";
import TurnstileWidget from "../forms/TurnstileWidget";
import { EventOverlay } from "./eventOverlay";
import { useOverlayDismiss } from "./useOverlayDismiss";

const FIELDS = ["name", "phone", "address"];
const IST = "Asia/Kolkata";

function formatPickupTime(iso) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    timeZone: IST,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const PickupRequestDrawer = ({ event, onClose }) => {
  const titleId = useId();
  const baseId = useId();
  const companyRef = useRef(null);
  const turnstileRef = useRef(null);
  const submitLockRef = useRef(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useOverlayDismiss(onClose);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (eventSubmit) => {
    eventSubmit.preventDefault();
    setSubmitError("");

    const next = {};
    for (const field of FIELDS) {
      const message = pickupFieldError(field, form[field]);
      if (message) next[field] = message;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      next.turnstile = "Please complete the security check.";
    }
    setErrors(next);
    const firstError = [...FIELDS, "turnstile"]
      .map((field) => next[field])
      .find(Boolean);
    if (firstError) {
      toastActionError(firstError);
      return;
    }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const payload = buildPickupRequestPayload(
        {
          name: form.name,
          phone: form.phone,
          address: form.address,
          company: companyRef.current?.value ?? "",
          turnstileToken,
        },
        { includeTurnstile: Boolean(TURNSTILE_SITE_KEY) },
      );
      const data = await submitPublicPickupRequest(event.slug, payload);
      setResult(data.pickupRequest);
      toastActionSuccess("Pickup request received.");
    } catch (err) {
      const message =
        err?.message || pickupRequestErrorMessage(err?.code, { field: err?.field });
      const field =
        err?.code === "turnstile_failed" || err?.field === "cf-turnstile-response"
          ? "turnstile"
          : err?.field;
      if (FIELDS.includes(field)) {
        setErrors({ [field]: message });
        setSubmitError("");
      } else {
        setErrors(field === "turnstile" ? { turnstile: message } : {});
        setSubmitError(message);
      }
      toastActionError(message);
      turnstileRef.current?.reset();
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const receivedAt = formatPickupTime(result?.createdAt);

  return (
    <EventOverlay>
      <div className="smym-drawer-backdrop" onClick={onClose} role="presentation">
        <aside
          className="smym-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(click) => click.stopPropagation()}
        >
          <div className="smym-drawer__head">
            <div className="min-w-0">
              <p className="smym-drawer__eyebrow">SMYM Chennai</p>
              <h2 id={titleId} className="smym-drawer__title">
                {result ? "Pickup request received" : "Request pickup"}
              </h2>
              {event?.name ? <p className="smym-drawer__sub">{event.name}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="smym-drawer__close"
              aria-label="Close"
            >
              <X size={20} aria-hidden />
            </button>
          </div>

          {result ? (
            <div className="smym-drawer__form">
              <div
                className="smym-drawer__body smym-reg-success"
                role="status"
                aria-live="polite"
              >
                <div className="smym-modal__icon" aria-hidden>
                  <BadgeCheck size={30} strokeWidth={2.25} />
                </div>
                <p className="smym-reg-success__lead">
                  Thanks{result.name ? `, ${result.name}` : ""}. Your pickup request was
                  received.
                </p>
                {receivedAt ? (
                  <p className="smym-drawer__pay-note">Received {receivedAt}.</p>
                ) : null}
              </div>
              <div className="smym-drawer__foot">
                <button type="button" onClick={onClose} className="smym-modal__dismiss">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="smym-drawer__form" noValidate>
              <div className="smym-drawer__body">
                <div>
                  <label htmlFor={`${baseId}-name`} className="smym-field__label">
                    Name <span className="smym-field__req">*</span>
                  </label>
                  <input
                    id={`${baseId}-name`}
                    type="text"
                    autoComplete="name"
                    maxLength={100}
                    value={form.name}
                    onChange={(input) => setField("name", input.target.value)}
                    className="smym-field__input"
                    aria-invalid={errors.name ? "true" : "false"}
                    aria-describedby={errors.name ? `${baseId}-name-error` : undefined}
                  />
                  {errors.name ? (
                    <p id={`${baseId}-name-error`} className="smym-field__error">
                      {errors.name}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor={`${baseId}-phone`} className="smym-field__label">
                    Phone <span className="smym-field__req">*</span>
                  </label>
                  <input
                    id={`${baseId}-phone`}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={20}
                    placeholder="10-digit mobile"
                    value={form.phone}
                    onChange={(input) => setField("phone", input.target.value)}
                    className="smym-field__input tabular-nums"
                    aria-invalid={errors.phone ? "true" : "false"}
                    aria-describedby={
                      errors.phone ? `${baseId}-phone-error` : `${baseId}-phone-hint`
                    }
                  />
                  {errors.phone ? (
                    <p id={`${baseId}-phone-error`} className="smym-field__error">
                      {errors.phone}
                    </p>
                  ) : (
                    <p id={`${baseId}-phone-hint`} className="smym-field__hint">
                      10-digit Indian mobile. +91, 91, or a leading 0 is fine.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor={`${baseId}-address`} className="smym-field__label">
                    Address <span className="smym-field__req">*</span>
                  </label>
                  <textarea
                    id={`${baseId}-address`}
                    rows={4}
                    autoComplete="street-address"
                    maxLength={500}
                    value={form.address}
                    onChange={(input) => setField("address", input.target.value)}
                    className="smym-field__input"
                    aria-invalid={errors.address ? "true" : "false"}
                    aria-describedby={
                      errors.address
                        ? `${baseId}-address-error`
                        : `${baseId}-address-hint`
                    }
                  />
                  {errors.address ? (
                    <p id={`${baseId}-address-error`} className="smym-field__error">
                      {errors.address}
                    </p>
                  ) : (
                    <p id={`${baseId}-address-hint`} className="smym-field__hint">
                      Where should we collect from?
                    </p>
                  )}
                </div>

                <div className="smym-hp" aria-hidden="true">
                  <label htmlFor={`${baseId}-company`}>Company</label>
                  <input
                    ref={companyRef}
                    id={`${baseId}-company`}
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    defaultValue=""
                  />
                </div>

                {TURNSTILE_SITE_KEY ? (
                  <div>
                    <TurnstileWidget
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onToken={(token) => {
                        setTurnstileToken(token);
                        setErrors((prev) => {
                          if (!prev.turnstile) return prev;
                          const rest = { ...prev };
                          delete rest.turnstile;
                          return rest;
                        });
                      }}
                      onError={(message) =>
                        setErrors((prev) => ({ ...prev, turnstile: message }))
                      }
                    />
                    {errors.turnstile ? (
                      <p className="smym-field__error">{errors.turnstile}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="smym-drawer__foot">
                {submitError ? (
                  <p className="smym-drawer__alert" role="alert">
                    {submitError}
                  </p>
                ) : null}
                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting ? "true" : "false"}
                  className="smym-btn smym-btn-primary smym-drawer__submit"
                >
                  {submitting ? "Sending…" : "Request pickup"}
                </button>
              </div>
            </form>
          )}
        </aside>
      </div>
    </EventOverlay>
  );
};

/** Opens the pickup form when the event has pickup enabled. */
const RequestPickupButton = ({ event, className }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!eventPickupEnabled(event)) return null;

  return (
    <>
      <button
        type="button"
        className={className}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <Truck className="h-4 w-4 shrink-0" aria-hidden />
        Request pickup
      </button>
      {open ? <PickupRequestDrawer event={event} onClose={close} /> : null}
    </>
  );
};

export default RequestPickupButton;
